import { getCdnUrl, getBackupCdnUrl } from 'owa-config';
import { logStartGreyError } from 'owa-analytics-start';
import { trace, type TraceErrorObject } from 'owa-trace';
import DOMPurify from 'dompurify';
import { findMetatag } from 'owa-metatags';

const externalSourceList: RegExp[] = [
    // /<anonymous>/ alone could match an anonymous callback to one of the Array methods (eg., Array.forEach())
    // However, /<anonymous>:\d+/ should only match extension code
    /<anonymous>:\d+/,
    /-extension:\/\//,
];

const markErrorAsExternal = (error: TraceErrorObject): void => {
    const { stack } = error;
    if (stack) {
        for (const source of externalSourceList) {
            // Mark errors as external from external sources like extensions, anonymous functions, etc.
            if (source.test(stack)) {
                error.external = true;
                break;
            }
        }
    }
};

const wildcardToRegex = (pattern: string): RegExp => {
    // Escape dots to match literal dots, then replace * with subdomain pattern
    // The pattern [^./]+ ensures wildcards only match valid subdomain characters (no dots or slashes)
    const escapedPattern = pattern.replace(/\./g, '\\.');
    const regexPattern = escapedPattern.replace(/\*/g, '[^./]+');
    // Anchor to start of string and make case-insensitive
    return new RegExp(`^${regexPattern}`, 'i');
};

const allowedTrustedTypesUrlPatterns: RegExp[] = [];

// Helper function to reset the cache for testing
export const resetAllowedTrustedTypesUrlPatterns = (): void => {
    allowedTrustedTypesUrlPatterns.length = 0;
};

// The list gets retrieved from a meta tag that is set from owa-web-server, includes URLs from ads and Microsoft CDNs.
export const isUrlInOwsAllowedTrustedTypesUrls = (url: string): boolean => {
    if (allowedTrustedTypesUrlPatterns.length === 0) {
        const allowedTrustedTypesUrlsMetaTag = findMetatag('allowedTrustedTypesUrls');

        if (allowedTrustedTypesUrlsMetaTag) {
            const urls = allowedTrustedTypesUrlsMetaTag.split(',');

            // Create regex patterns for wildcard matching
            allowedTrustedTypesUrlPatterns.push(
                ...urls.map(urlPattern => wildcardToRegex(urlPattern.trim()))
            );
        }
    }

    // Check against regex patterns for wildcard matching
    for (const pattern of allowedTrustedTypesUrlPatterns) {
        if (pattern.test(url)) {
            return true;
        }
    }

    return false;
};

export const defaultCreateHTML = (
    html: string,
    type: string,
    sink: string,
    isTrustedTypesEnabled: boolean
): string => {
    if (html === '') {
        return '';
    }

    const error: TraceErrorObject = new Error(
        "[Default Policy] This document requires 'TrustedHTML' assignment."
    );
    markErrorAsExternal(error);

    trace.warn(`${error.message}: ${error.stack}`);

    logStartGreyError('TrustedTypesHTMLDefaultPolicyError', error, {
        type,
        sink,
        isHTMLSanitized: isTrustedTypesEnabled,
    });

    if (isTrustedTypesEnabled) {
        // If the HTML is untrusted, we sanitize it by only allowing safe HTMLs elements
        return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    }
    return html;
};

export const defaultCreateScriptUrl = (
    url: string,
    type: string,
    sink: string,
    isTrustedTypesEnabled: boolean
): string => {
    const cdnUrl = `https:${getCdnUrl()}`;
    const backupCdnUrl = `https:${getBackupCdnUrl()}`;
    const currentDomain = window.location.origin;
    const blobUrl = `blob:${currentDomain}`;

    if (
        url.startsWith(currentDomain) ||
        url.startsWith(cdnUrl) ||
        url.startsWith(backupCdnUrl) ||
        url.startsWith(blobUrl) ||
        isUrlInOwsAllowedTrustedTypesUrls(url)
    ) {
        return url;
    }

    const error = new Error(
        "[Default Policy] This document requires 'TrustedScriptURL' assignment."
    );

    trace.warn(`${error.message}: ${error.stack}`);

    logStartGreyError('TrustedTypesScriptURLDefaultPolicyError', error, {
        url,
        type,
        sink,
        isScriptURLBlocked: isTrustedTypesEnabled,
    });

    if (isTrustedTypesEnabled) {
        // If the script is untrusted, we throw an error to block the script execution
        throw error;
    }
    return url;
};

export const defaultCreateScript = (
    script: string,
    type: string,
    sink: string,
    isTrustedTypesEnabled: boolean
): string => {
    const error = new Error("[Default Policy] This document requires 'TrustedScript' assignment.");
    markErrorAsExternal(error);

    trace.warn(`${error.message}: ${error.stack}`);

    logStartGreyError('TrustedTypesScriptDefaultPolicyError', error, {
        script,
        type,
        sink,
        isScriptBlocked: isTrustedTypesEnabled,
    });

    if (isTrustedTypesEnabled) {
        // If the script URL is untrusted, we throw an error to block the script execution
        throw error;
    }
    return script;
};
